<template>
  <div v-if="keyDetails.length" class="key-details">
    <template v-for="(detail, index) in keyDetails">
      <div v-if="detail.value" :key="index" class="key-details__row">
        <template v-if="detail.ticketModal === ticketsModals.DLP_FINDINGS_MODAL">
          <div
            role="button"
            class="coro-link key-details__findings"
            :data-testid="`device-preview-key-details-${kebabCase(detail.titleKey)}-findings`"
            @click="openDlpFindingsModal()"
          >
            {{ $t("ticketDetails.showFindings") }}
          </div>
        </template>

        <template v-else>
          <div class="key-details__key mr-4">
            <span
              class="subtitle2"
              :data-testid="`device-preview-key-details-${kebabCase(detail.titleKey)}-title`"
              v-html="detail.titleKey"
            />
            <v-tooltip v-if="detail.tooltip" open-delay="300" location="top">
              <template #activator="{ props }">
                <v-icon
                  v-bind="props"
                  icon="$infoFilled"
                  size="16"
                  class="key-details__info-icon"
                  :data-testid="`device-preview-key-details-${kebabCase(detail.titleKey)}-tooltip-icon`"
                />
              </template>
              <span>{{ detail.tooltip }}</span>
            </v-tooltip>
          </div>
          <div>
            <div v-if="detail.ticketModal" class="mb-4 d-flex align-center">
              <template v-if="detail.showItemsNumber">
                <div
                  class="body2"
                  :class="{ 'd-inline': index === detail.showItemsNumber - 1 }"
                  v-for="(item, index) in getStringItems(detail).slice(0, detail.showItemsNumber)"
                  :key="item"
                  :data-testid="`device-preview-key-details-${kebabCase(detail.titleKey)}-modal-value`"
                >
                  {{ item }}
                </div>
                <v-tooltip open-delay="100" location="top">
                  <template #activator="{ props }">
                    <span
                      v-bind="props"
                      v-if="detail.value.length > 1"
                      role="button"
                      class="plus-one-text ml-2"
                      :data-testid="`device-preview-key-details-${kebabCase(
                        detail.titleKey
                      )}-open-modal-btn`"
                      >&nbsp;+{{ detail.value.length - detail.showItemsNumber }}
                    </span>
                  </template>
                  <ul>
                    <li
                      v-for="(listItem, index) in detail.value.slice(
                        detail.showItemsNumber,
                        detail.value.length
                      )"
                      :key="index"
                    >
                      <span class="text-white" v-text="listItem" />
                    </li>
                  </ul>
                </v-tooltip>
              </template>
              <template v-else>
                <span
                  class="body2 mr-1"
                  :data-testid="`device-preview-key-details-${kebabCase(detail.titleKey)}-modal-value`"
                  >{{ detail.value[0] }}</span
                >
                <v-tooltip open-delay="100" location="top">
                  <template #activator="{ props }">
                    <span
                      v-bind="props"
                      v-if="detail.value.length > 1"
                      role="button"
                      class="plus-one-text ml-2"
                      :data-testid="`device-preview-key-details-${kebabCase(
                        detail.titleKey
                      )}-open-modal-btn`"
                      >&nbsp;+{{ detail.value.length - 1 }}
                    </span>
                  </template>
                  <ul>
                    <li
                      v-for="(listItem, index) in detail.value.slice(
                        detail.showItemsNumber,
                        detail.value.length
                      )"
                      :key="index"
                    >
                      <span class="text-white" v-text="listItem" />
                    </li>
                  </ul>
                </v-tooltip>
              </template>
            </div>

            <div v-else-if="detail.chipList" class="mb-4">
              <div class="d-flex flex-wrap gap-1">
                <span
                  v-for="item in getVisibleChips(detail, index)"
                  :key="item"
                  class="chip-label body2 recipient-label"
                  :data-testid="`device-preview-key-details-${kebabCase(detail.titleKey)}-chip`"
                  >{{ item }}</span
                >
              </div>
              <div
                v-if="getStringItems(detail).length > CHIP_SHOW_LIMIT"
                role="button"
                class="d-flex align-center mt-1"
                :data-testid="`device-preview-key-details-${kebabCase(detail.titleKey)}-toggle`"
                @click="toggleChipExpand(index)"
              >
                <span class="coro-link">
                  {{
                    expandedChipRows.has(index)
                      ? $t("general.showLess")
                      : $t("general.showAllN", { n: getStringItems(detail).length })
                  }}
                </span>
                <v-icon size="16" class="ml-1">
                  {{ expandedChipRows.has(index) ? "$chevronUp" : "$chevronDown" }}
                </v-icon>
              </div>
            </div>

            <div v-else class="body2 mb-4">
              <div v-if="detail.loopWithIcon">
                <div
                  v-for="item in getListItems(detail)"
                  :key="item.label"
                  class="d-flex justify-start align-baseline"
                >
                  <v-icon size="16" class="mr-1">{{ item.icon }}</v-icon>
                  <span
                    class="mb-2"
                    :data-testid="`device-preview-key-details-${kebabCase(detail.titleKey)}-list-item`"
                    v-html="item.label"
                  ></span>
                </div>
              </div>
              <div
                v-else
                class="word-break-all"
                :data-testid="`device-preview-key-details-${kebabCase(detail.titleKey)}-value`"
                v-html="detail.value"
              ></div>
            </div>
          </div>
        </template>
      </div>
    </template>
  </div>
</template>
<script lang="ts">
import { mapKeyDetails } from "@/_helpers/ticket-key-details";
import { computed, defineComponent, onMounted, ref, type PropType } from "vue";
import type { Finding, TicketDetails } from "@/_store/tickets/tickets.module";
import type { TicketKeyDetail, TicketKeyDetailListItem } from "@/_helpers/ticket-key-details";
import { TicketsModal, TicketType } from "@/constants/tickets";
import { useTicketPreviewGeneralInfo } from "@/composables/useTicketPreviewGeneralInfo";
import { useTicketStore } from "@/_store/tickets/ticket.module";
import { useMspTicketsStore } from "@/_store/msp/tickets/msp-tickets.module";
import { AppContext } from "@/types";
import { useI18n } from "vue-i18n";
import kebabCase from "lodash/kebabCase";

const CHIP_SHOW_LIMIT = 3;

export default defineComponent({
  props: {
    ticket: {
      type: Object as PropType<TicketDetails>,
      required: true,
    },
    context: {
      type: String as PropType<AppContext>,
      default: AppContext.SINGLE_WORKSPACE,
    },
  },
  setup(props) {
    const { openListModal } = useTicketPreviewGeneralInfo(props.ticket);
    const i18n = useI18n();
    const sharedFiles = ref<string[]>([]);
    const keyDetails = computed(() => {
      const details = mapKeyDetails(props.ticket);
      //@TODO Remove once BE will send a proper findings
      if (sharedFiles.value.length) {
        const sharedWithIndex = details.findIndex(
          (detail) => detail.ticketModal === TicketsModal.SHARED_WITH
        );
        if (sharedWithIndex !== -1) {
          details.splice(sharedWithIndex + 1, 0, {
            titleKey: i18n.t("ticketDetails.sharedFiles"),
            value: sharedFiles.value,
            ticketModal: TicketsModal.SHARED_WITH,
          });
        }
      }
      return details;
    });
    const expandedChipRows = ref(new Set<number>());

    //@TODO Remove once BE will send a proper findings
    onMounted(async () => {
      if (
        props.ticket.eventType !== TicketType.SUSPECTED_UNIFIED_SENSITIVE_DATA_VIOLATION_BY_SHARING
      ) {
        return;
      }
      const store = props.context === AppContext.MSP ? useMspTicketsStore() : useTicketStore();
      try {
        const { data } = await store.getSharedFileNames(props.ticket.eventId);
        sharedFiles.value = data ?? [];
      } catch (e) {
        console.error(e);
      }
    });

    const toggleChipExpand = (index: number) => {
      const next = new Set(expandedChipRows.value);
      if (next.has(index)) {
        next.delete(index);
      } else {
        next.add(index);
      }
      expandedChipRows.value = next;
    };

    // Narrows a list-valued row's value to its string items, keeping the cast
    // out of the template (see also `getListItems` for icon rows).
    const getStringItems = (detail: TicketKeyDetail): string[] => detail.value as string[];

    const getVisibleChips = (detail: TicketKeyDetail, index: number): string[] => {
      const items = getStringItems(detail);
      return expandedChipRows.value.has(index) ? items : items.slice(0, CHIP_SHOW_LIMIT);
    };

    // Narrows a `loopWithIcon` row's value to its list items for the template.
    const getListItems = (detail: TicketKeyDetail): TicketKeyDetailListItem[] =>
      detail.value as TicketKeyDetailListItem[];

    const openDlpFindingsModal = () => {
      openListModal(
        TicketsModal.DLP_FINDINGS_MODAL,
        props.ticket.sections.findings.map((finding) => {
          return {
            template: createFindingsModalTemplate(finding),
          };
        })
      );
    };

    function createFindingsModalTemplate(finding: Finding) {
      let template = i18n.t(`modals.${TicketsModal.DLP_FINDINGS_MODAL}.type`, {
        type: i18n.t(`ticketDetails.incidentFindings.type.${finding.type}`),
      });
      if (finding.longQuote) {
        // if quote present, highlight it, otherwise show longQuote as it is
        const quoteIndex = finding.quote ? finding.longQuote.indexOf(finding.quote) : -1;
        if (finding.quote && quoteIndex !== -1) {
          const highlightedTextEndIndex = quoteIndex + finding.quote.length;
          const textBeforeHighlight = finding.longQuote.substring(0, quoteIndex);
          const textAfterHighlight = finding.longQuote.substring(highlightedTextEndIndex);
          const highlightedText = finding.quote;
          template =
            template +
            i18n.t(`modals.${TicketsModal.DLP_FINDINGS_MODAL}.longQuote`, {
              textBeforeHighlight,
              textAfterHighlight,
              highlightedText,
            });
        } else {
          template =
            template +
            i18n.t("modals.dlpFindings.longQuoteWithoutHighlight", {
              longQuote: finding.longQuote,
            });
        }
      } else if (finding.quote) {
        template =
          template +
          i18n.t(`modals.${TicketsModal.DLP_FINDINGS_MODAL}.quote`, {
            quote: finding.quote,
          });
      }
      return template;
    }

    return {
      kebabCase,
      ticketsModals: TicketsModal,
      keyDetails,
      openDlpFindingsModal,
      openListModal,
      expandedChipRows,
      toggleChipExpand,
      getVisibleChips,
      getListItems,
      getStringItems,
      CHIP_SHOW_LIMIT,
    };
  },
});
</script>

<style lang="scss" scoped>
.service-icon {
  width: 40px;
  height: 40px;
}

ul {
  list-style-type: none;
}

.plus-one-text {
  background-color: rgb(var(--v-theme-indigo-instructional));
  padding: 4px 6px 4px 4px;
  border-radius: 50%;
  font-size: 11px;
  margin-left: 2px;
  height: 24px;
  min-width: 24px;
}

.key-details {
  display: grid;
  grid-template-columns: max-content 1fr;
  column-gap: 8px;
  align-items: baseline;
}

.key-details__row {
  display: contents;
}

.key-details__findings {
  grid-column: 1 / -1;
}

.key-details__key {
  display: flex;
  align-items: center;
  gap: 4px;
}

.key-details__info-icon::before {
  color: rgb(var(--v-theme-indigo-light));
}

.chip-label {
  display: inline-block;
  background-color: rgb(var(--v-theme-indigo-faint));
  border: 1px solid rgb(var(--v-theme-indigo-pale));
  border-radius: 4px;
  padding: 2px 8px;

  &:not(:first-child) {
    margin-left: 8px;
  }
}

:deep(.icon-chevron-down-xs:before),
:deep(.icon-chevron-up-xs:before) {
  color: rgb(var(--v-theme-link));
}

:deep(.url-chip) {
  display: inline-block;
  background-color: rgb(var(--v-theme-indigo-faint));
  border: 1px solid rgb(var(--v-theme-indigo-pale));
  border-radius: 4px;
  padding: 0 4px;
}
</style>
